/**
 * Localization suggestion banner.
 *
 * Detection strategy, in order:
 *   1. Shopify's own geolocation endpoint (/browsing_context_suggestions.json) —
 *      IP-derived on Shopify's infra, so we never touch the raw IP ourselves.
 *      Works on stores with Markets/Geolocation enabled.
 *   2. navigator.language as a fallback when the endpoint is unavailable or
 *      returns no usable country. Privacy-friendly but only fires when the
 *      browser tag carries an explicit region (e.g. `de-AT`).
 *
 * Dismissals persist in localStorage for 30 days, keyed by the country we were
 * suggesting at the time — so a traveler who dismisses while abroad still sees
 * the banner again once they're home and detection flips back.
 */
(async () => {
  const STORAGE_KEY = 'weck_loc_suggestion_dismissed';
  const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const banner = document.querySelector('[data-localization-suggestion]');
  if (!banner) return;

  let payload;
  try {
    payload = JSON.parse(banner.dataset.suggestionPayload);
  } catch (err) {
    return;
  }
  if (!payload || !payload.candidates) return;

  const detectedCountry = await detectCountryFromShopify();
  const suggestion = detectedCountry
    ? pickByCountry(payload, detectedCountry)
    : pickSuggestion(payload, navigator.languages || [navigator.language]);
  if (!suggestion) return;
  if (isDismissedFor(suggestion.country)) return;

  fillBanner(banner, suggestion);
  showBanner(banner);

  banner.querySelectorAll('[data-suggestion-close], [data-suggestion-stay]').forEach((btn) => {
    btn.addEventListener('click', () => {
      rememberDismissal(suggestion.country);
      hideBanner(banner);
    });
  });

  banner.querySelectorAll('[data-suggestion-accept]').forEach((btn) => {
    btn.addEventListener('click', () => acceptSuggestion(suggestion));
  });

  function readDismissal() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.ts !== 'number') return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function isDismissedFor(countryISO) {
    const d = readDismissal();
    if (!d) return false;
    if (Date.now() - d.ts > DISMISS_TTL_MS) return false;
    return (d.country || '').toUpperCase() === (countryISO || '').toUpperCase();
  }

  function rememberDismissal(countryISO) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ts: Date.now(),
        country: (countryISO || '').toUpperCase()
      }));
    } catch (err) {
      /* private mode / quota — silently noop */
    }
  }

  /**
   * Ask Shopify's own geolocation endpoint for the visitor's likely country.
   * The endpoint is populated by Shopify-side IP lookup; we never see the IP.
   * Returns an uppercase ISO country code (e.g. "AT") or null on any failure.
   */
  async function detectCountryFromShopify() {
    try {
      const res = await fetch('/browsing_context_suggestions.json', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return null;
      const data = await res.json();
      const iso = data?.detected_values?.country?.handle
        || data?.detected_values?.country?.code
        || null;
      return iso ? iso.toUpperCase() : null;
    } catch (err) {
      return null;
    }
  }

  /**
   * Build a suggestion when we have a confident server-detected country.
   * Skips if the detected country is already current or isn't in our visible
   * candidate list. Language preference still defers to the browser's primary
   * subtag so a German tourist in Austria gets the German variant of AT.
   */
  function pickByCountry(data, countryISO) {
    const currentCountry = (data.current.country || '').toUpperCase();
    const currentLang = (data.current.language || '').toLowerCase();
    if (!countryISO || countryISO === currentCountry) return null;

    const candidate = data.candidates.find((c) => c.iso.toUpperCase() === countryISO);
    if (!candidate) return null;

    const browserPrimary = (navigator.language || '').toLowerCase().split('-')[0];
    const lang_match = (candidate.languages || []).find((l) => l.toLowerCase().split('-')[0] === browserPrimary)
      || candidate.languages?.[0]
      || currentLang;

    return {
      country: candidate.iso.toUpperCase(),
      countryName: candidate.name,
      language: lang_match
    };
  }

  /**
   * Pick the best alternative country+language from the visitor's browser
   * languages. We only suggest a switch when the browser explicitly names a
   * region (e.g. `de-AT`, `nl-BE`) that matches a different market in our
   * candidate list. A bare language tag (`de`, `en`) is too weak a signal —
   * a German Chrome on the DE store shouldn't be steered to Austria just
   * because Austria also has German available.
   */
  function pickSuggestion(data, browserLangs) {
    const currentCountry = (data.current.country || '').toUpperCase();
    const currentLang = (data.current.language || '').toLowerCase();

    for (const raw of browserLangs) {
      if (!raw) continue;
      const tag = raw.toLowerCase();
      const [lang, region] = tag.split('-');
      if (!region) continue;

      const regionUpper = region.toUpperCase();
      if (regionUpper === currentCountry) return null;

      const candidate = data.candidates.find((c) => c.iso.toUpperCase() === regionUpper);
      if (!candidate) continue;

      const lang_match = (candidate.languages || []).find((l) => l.toLowerCase().split('-')[0] === lang)
        || candidate.languages?.[0]
        || currentLang;

      return {
        country: candidate.iso.toUpperCase(),
        countryName: candidate.name,
        language: lang_match
      };
    }
    return null;
  }

  function fillBanner(root, sug) {
    const titles = root.querySelectorAll('[data-suggestion-title]');
    const subtitles = root.querySelectorAll('[data-suggestion-subtitle]');
    const acceptLabels = root.querySelectorAll('[data-suggestion-accept-label]');
    const i18n = root.dataset;

    titles.forEach((el) => {
      el.textContent = template(getStr('title'), { country: sug.countryName });
    });
    subtitles.forEach((el) => {
      el.textContent = template(getStr('subtitle'), { country: sug.countryName });
    });
    acceptLabels.forEach((el) => {
      el.textContent = template(getStr('accept'), { country: sug.countryName });
    });

    function getStr(key) {
      const fallbacks = {
        title: 'Es sieht aus, als wärst du in __COUNTRY__.',
        subtitle: 'Möchtest du zur lokalen Version wechseln?',
        accept: 'Zur __COUNTRY__ Version'
      };
      return i18n['suggestion' + key.charAt(0).toUpperCase() + key.slice(1)] || fallbacks[key];
    }
  }

  function template(str, vars) {
    return str.replace(/__([A-Z]+)__/g, (_, k) => vars[k.toLowerCase()] ?? '');
  }

  function showBanner(root) {
    root.hidden = false;
    root.classList.remove('hidden');
    requestAnimationFrame(() => {
      root.classList.remove('opacity-0', 'translate-y-2');
      root.classList.add('opacity-100', 'translate-y-0');
    });
  }

  function hideBanner(root) {
    root.classList.add('opacity-0', 'translate-y-2');
    root.classList.remove('opacity-100', 'translate-y-0');
    setTimeout(() => {
      root.classList.add('hidden');
      root.hidden = true;
    }, 200);
  }

  function acceptSuggestion(sug) {
    const form = document.querySelector('[data-suggestion-form]');
    if (!form) return;
    form.querySelector('[data-suggestion-country-input]').value = sug.country;
    form.querySelector('[data-suggestion-locale-input]').value = sug.language;
    rememberDismissal(sug.country);
    form.submit();
  }
})();
